import type { FunctionComponent } from 'react';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { useMemberId } from '@trello/authentication';
import {
  boardSwitcherSearchSharedState,
  boardSwitcherSharedState,
  keyboardNavSharedState,
} from '@trello/board-switcher';
import { sortBoards } from '@trello/boards';
import { useSharedStateSelector } from '@trello/shared-state';
import { buildFuzzyMatcher } from '@trello/strings';
import { getTestId, type BoardSwitcherTestIds } from '@trello/test-ids';

import { useBoardSwitcherMemberFragment } from './BoardSwitcherMemberFragment.generated';
import { BoardSwitcherTile } from './BoardSwitcherTile';
import { BoardSwitcherTiles } from './BoardSwitcherTiles';
import { useBoardSwitcher } from './useBoardSwitcher';

import * as styles from './SearchResults.module.less';

export const SearchResults: FunctionComponent = () => {
  const memberId = useMemberId();

  const activeId = useSharedStateSelector(
    keyboardNavSharedState,
    useCallback((state) => state.activeId, []),
  );

  const query = useSharedStateSelector(
    boardSwitcherSearchSharedState,
    useCallback((value) => value.query, []),
  );
  const selectedWorkspaceId = useSharedStateSelector(
    boardSwitcherSharedState,
    useCallback((value) => value.selectedWorkspaceId, []),
  );

  const { data } = useBoardSwitcherMemberFragment({
    from: { id: memberId },
  });

  const { recentBoards, starredBoards } = useBoardSwitcher();

  const myStarredBoardIds = useMemo(() => {
    return starredBoards.map(({ idBoard }) => idBoard);
  }, [starredBoards]);

  const myRecentBoardIds = useMemo(() => {
    if (!recentBoards) return [];
    return recentBoards.map(({ id }) => id);
  }, [recentBoards]);

  const isMatch = buildFuzzyMatcher(query ?? '');

  const boardMatches = useMemo(() => {
    if (!data?.boards) return [];

    const matches = data.boards.filter(({ name, idOrganization }) => {
      const isWorkspaceMatch = idOrganization === selectedWorkspaceId;
      return isMatch(name) && (!selectedWorkspaceId || isWorkspaceMatch);
    });

    return sortBoards({
      boards: matches,
      myStarredBoardIds,
      myRecentBoardIds,
      searchQuery: query ?? '',
    });
  }, [
    data?.boards,
    isMatch,
    query,
    myRecentBoardIds,
    myStarredBoardIds,
    selectedWorkspaceId,
  ]);

  if (!query) {
    return null;
  }

  if (boardMatches.length === 0) {
    return (
      <div
        className={styles.noResults}
        data-testid={getTestId<BoardSwitcherTestIds>(
          'board-switcher-search-results',
        )}
      >
        <p>
          <FormattedMessage
            id="templates.board_switcher.no-matches"
            defaultMessage="No matches."
            description="Text shown when there are no search results"
          />
        </p>
      </div>
    );
  }

  return (
    <BoardSwitcherTiles
      className={styles.searchResults}
      testId={getTestId<BoardSwitcherTestIds>('board-switcher-search-results')}
    >
      {boardMatches.map(({ id }, i) => (
        <BoardSwitcherTile
          key={id}
          boardId={id}
          section="search"
          isForcedActive={i === 0 && !activeId}
        />
      ))}
    </BoardSwitcherTiles>
  );
};
